import Colors from "Constants/Color";
import { tableStyles } from "Constants/Style";
import { Axios } from "api/axios";
import ActionButton from "components/Buttons/ActionButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import { api } from "helpers/api_url";
import FilePreviewModal from "pages/Warehouse/ItemInward2/GRNDetails/FilePreviewModal/FilePreviewModal";
import React, { useEffect, useState, useMemo } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { showConfirm } from "utils/swalHelper";

const SalesOrderReturn: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch_id } = useParams<{ dispatch_id?: string }>();
  const dispatch = useDispatch<any>();
  const [data, setData] = useState<any>({});
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [processing, setProcessing] = useState<boolean>(false);
  const [revertingItemId, setRevertingItemId] = useState<string | null>(null);
  const [returnQuantities, setReturnQuantities] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    setIsLoadingInitialData(true);
    Axios.get(
      `${api?.warehouse?.dispatch?.dispatch_done?.dispatch_by_id_for_view}/${dispatch_id}/view`
    )
      .then((result) => {
        setData(result?.data?.data || {});
      })
      .catch((err) => {
        toast.error("Failed to fetch Dispatch Data");
        console.log("Failed to fetch Dispatch Data", err);
      })
      .finally(() => {
        setIsLoadingInitialData(false);
      });
  }, [dispatch_id]);

  const handleSelectItem = (itemId: string) => {
    // Find the item to check if it's returned
    const item = data?.dispatch_items?.find((item: any) => {
      const id = item.dispatch_item_id || item.id || item.dispatch_items_id;
      return id === itemId;
    });

    // Prevent selecting returned items
    if (
      item?.dispatch_item_status?.toLowerCase() === "sales order return" ||
      item?.dispatch_item_status?.toLowerCase() ===
        "sales order return inwarded"
    ) {
      return;
    }

    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        // Remove return quantity when item is deselected
        setReturnQuantities((prev) => {
          const newQuantities = { ...prev };
          delete newQuantities[itemId];
          return newQuantities;
        });
        return prev.filter((id) => id !== itemId);
      } else {
        // Initialize return quantity with dispatch quantity when item is selected
        const selectedItem = data?.dispatch_items?.find((item: any) => {
          const id = item.dispatch_item_id || item.id || item.dispatch_items_id;
          return id === itemId;
        });
        // Handle Decimal type or string conversion
        const dispatchQty = selectedItem?.dispatch_quantity
          ? parseFloat(String(selectedItem.dispatch_quantity))
          : 0;
        setReturnQuantities((prev) => ({
          ...prev,
          [itemId]: dispatchQty,
        }));
        return [...prev, itemId];
      }
    });
  };

  const handleSelectAll = () => {
    // Get only non-returned items (exclude items with "sales order return" status)
    const nonReturnedItems =
      data?.dispatch_items?.filter(
        (item: any) =>
          item?.dispatch_item_status?.toLowerCase() !== "sales order return" &&
          item?.dispatch_item_status?.toLowerCase() !==
            "sales order return inwarded"
      ) || [];
    const nonReturnedItemIds = nonReturnedItems.map(
      (item: any) => item.dispatch_item_id || item.id || item.dispatch_items_id
    );

    // Check if all non-returned items are already selected
    const allNonReturnedSelected =
      nonReturnedItemIds.length > 0 &&
      nonReturnedItemIds.every((id: string) => selectedItems.includes(id));

    if (allNonReturnedSelected) {
      // Deselect all non-returned items and clear their return quantities
      setSelectedItems((prev) =>
        prev.filter((id) => !nonReturnedItemIds.includes(id))
      );
      setReturnQuantities((prev) => {
        const newQuantities = { ...prev };
        nonReturnedItemIds.forEach((id: string) => {
          delete newQuantities[id];
        });
        return newQuantities;
      });
    } else {
      // Select all non-returned items and initialize return quantities with dispatch quantities
      setSelectedItems((prev) => {
        const returnedItemIds = prev.filter(
          (id) => !nonReturnedItemIds.includes(id)
        );
        return [...returnedItemIds, ...nonReturnedItemIds];
      });
      // Initialize return quantities for newly selected items
      setReturnQuantities((prev) => {
        const newQuantities = { ...prev };
        nonReturnedItemIds.forEach((itemId: string) => {
          // Only set if not already set
          if (!newQuantities[itemId]) {
            const item = nonReturnedItems.find(
              (item: any) =>
                (item.dispatch_item_id || item.id || item.dispatch_items_id) ===
                itemId
            );
            const dispatchQty = item?.dispatch_quantity
              ? parseFloat(String(item.dispatch_quantity))
              : 0;
            newQuantities[itemId] = dispatchQty;
          }
        });
        return newQuantities;
      });
    }
  };

  const allNonReturnedSelected = useMemo(() => {
    if (!data?.dispatch_items || data.dispatch_items.length === 0) {
      return false;
    }
    // Only consider items that don't have "sales order return" status
    const nonReturnedItems = data.dispatch_items.filter(
      (item: any) =>
        item?.dispatch_item_status?.toLowerCase() !== "sales order return" &&
        item?.dispatch_item_status?.toLowerCase() !==
          "sales order return inwarded"
    );
    const nonReturnedItemIds = nonReturnedItems.map(
      (item: any) => item.dispatch_item_id || item.id || item.dispatch_items_id
    );

    return (
      nonReturnedItemIds.length > 0 &&
      nonReturnedItemIds.every((id: string) => selectedItems.includes(id))
    );
  }, [data?.dispatch_items, selectedItems]);

  // Check if any items can be reverted
  const hasRevertableItems = useMemo(() => {
    if (!data?.dispatch_items || data.dispatch_items.length === 0) {
      return false;
    }
    return data.dispatch_items.some(
      (item: any) =>
        item?.dispatch_item_status?.toLowerCase() === "sales order return"
    );
  }, [data?.dispatch_items]);

  // Check if any items can have return quantity (not DISPATCHED, not "sales order return", not "sales order return inwarded")
  const hasReturnableItems = useMemo(() => {
    if (!data?.dispatch_items || data.dispatch_items.length === 0) {
      return false;
    }

    // Show column if there are any selected items that are not "sales order return" or "sales order return inwarded"
    if (selectedItems.length > 0) {
      const hasSelectableNonReturned = selectedItems.some((itemId: string) => {
        const item = data.dispatch_items.find((item: any) => {
          const id = item.dispatch_item_id || item.id || item.dispatch_items_id;
          return id === itemId;
        });
        const status = item?.dispatch_item_status?.toLowerCase();
        return (
          status !== "sales order return" &&
          status !== "sales order return inwarded"
        );
      });
      if (hasSelectableNonReturned) return true;
    }

    // Also show if there are any items that are not DISPATCHED, not "sales order return", and not "sales order return inwarded"
    return data.dispatch_items.some((item: any) => {
      const status = item?.dispatch_item_status?.toLowerCase();
      return (
        status !== "dispatched" &&
        status !== "sales order return" &&
        status !== "sales order return inwarded"
      );
    });
  }, [data?.dispatch_items, selectedItems]);

  // Calculate totals for selected items
  const recalculatedTotals = useMemo(() => {
    if (!data?.dispatch_items || selectedItems.length === 0) {
      return {
        subtotal_before_discount: Number(data?.subtotal_before_discount) || 0,
        total_item_discounts: Number(data?.total_item_discounts) || 0,
        net_taxable_value: Number(data?.net_taxable_value) || 0,
        total_gst: Number(data?.total_gst) || 0,
        total_amount: Number(data?.total_amount) || 0,
      };
    }

    const selectedItemsData = data.dispatch_items.filter((item: any) => {
      const itemId = item.dispatch_item_id || item.id || item.dispatch_items_id;
      return selectedItems.includes(itemId);
    });

    const selectedSubtotalBeforeDiscount = selectedItemsData.reduce(
      (sum: number, item: any) => {
        return sum + (Number(item.amount) || 0);
      },
      0
    );

    const selectedTotalItemDiscounts = selectedItemsData.reduce(
      (sum: number, item: any) => {
        return sum + (Number(item.discount_amount) || 0);
      },
      0
    );

    const selectedNetTaxableValue =
      selectedSubtotalBeforeDiscount - selectedTotalItemDiscounts;

    const selectedTotalGst = selectedItemsData.reduce(
      (sum: number, item: any) => {
        return sum + (Number(item.gst_amount) || 0);
      },
      0
    );

    const selectedTotalAmount = selectedItemsData.reduce(
      (sum: number, item: any) => {
        return sum + (Number(item.total_amount) || 0);
      },
      0
    );

    return {
      subtotal_before_discount: parseFloat(
        selectedSubtotalBeforeDiscount.toFixed(2)
      ),
      total_item_discounts: parseFloat(selectedTotalItemDiscounts.toFixed(2)),
      net_taxable_value: parseFloat(selectedNetTaxableValue.toFixed(2)),
      total_gst: parseFloat(selectedTotalGst.toFixed(2)),
      total_amount: parseFloat(selectedTotalAmount.toFixed(2)),
    };
  }, [data, selectedItems]);

  const handleSalesOrderReturn = async () => {
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to return");
      return;
    }

    // Validate return quantities
    const invalidItems: string[] = [];
    selectedItems.forEach((itemId) => {
      const returnQty = returnQuantities[itemId] || 0;
      const item = data?.dispatch_items?.find((item: any) => {
        const id = item.dispatch_item_id || item.id || item.dispatch_items_id;
        return id === itemId;
      });
      const dispatchQty = parseFloat(item?.dispatch_quantity) || 0;

      if (returnQty <= 0) {
        invalidItems.push(item?.item_name || itemId);
      } else if (returnQty > dispatchQty) {
        invalidItems.push(
          `${item?.item_name || itemId} (return qty > dispatch qty)`
        );
      }
    });

    if (invalidItems.length > 0) {
      toast.error(
        `Please enter valid return quantities for: ${invalidItems.join(", ")}`
      );
      return;
    }

    showConfirm(
      `Do you really want to return ${selectedItems.length} selected item(s)?`,
      "Are you sure?",
      "Yes, return it!",
      "No, cancel"
    ).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          setProcessing(true);
          const payload = {
            dispatch_items: selectedItems.map((itemId) => ({
              dispatch_item_id: itemId,
              return_quantity: parseFloat(
                returnQuantities[itemId]?.toString() || "0"
              ),
            })),
          };
          console.log("payload : ", payload);
          await Axios.patch(
            `${api?.warehouse?.dispatch?.dispatch_done?.sales_order_return}`,
            payload
          );

          toast.success("Selected items have been returned successfully.");

          // Clear selected items and return quantities
          setSelectedItems([]);
          setReturnQuantities({});

          // Refresh the data
          setIsLoadingInitialData(true);
          Axios.get(
            `${api?.warehouse?.dispatch?.dispatch_done?.dispatch_by_id_for_view}/${dispatch_id}/view`
          )
            .then((result) => {
              setData(result?.data?.data || {});
            })
            .catch((err) => {
              toast.error("Failed to fetch Dispatch Data");
              console.log("Failed to fetch Dispatch Data", err);
            })
            .finally(() => {
              setIsLoadingInitialData(false);
            });
        } catch (err: any) {
          const errorMessage =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to process sales order return. Try again.";
          toast.error(errorMessage);
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const handleRevertItem = async (itemId: string) => {
    showConfirm(
      "Do you really want to revert this item back to DISPATCHED status?",
      "Are you sure?",
      "Yes, revert it!",
      "No, cancel"
    ).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          setRevertingItemId(itemId);
          const payload = {
            dispatch_item_ids: [itemId],
          };

          await Axios.patch(
            `${api?.warehouse?.dispatch?.dispatch_done?.revert_sales_order_return}`,
            payload
          );

          toast.success(
            "Item has been reverted to DISPATCHED status successfully."
          );

          // Refresh the data
          setIsLoadingInitialData(true);
          Axios.get(
            `${api?.warehouse?.dispatch?.dispatch_done?.dispatch_by_id_for_view}/${dispatch_id}/view`
          )
            .then((result) => {
              setData(result?.data?.data || {});
            })
            .catch((err) => {
              toast.error("Failed to fetch Dispatch Data");
              console.log("Failed to fetch Dispatch Data", err);
            })
            .finally(() => {
              setIsLoadingInitialData(false);
            });
        } catch (err: any) {
          const errorMessage =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to revert item. Try again.";
          toast.error(errorMessage);
        } finally {
          setRevertingItemId(null);
        }
      }
    });
  };

  console.log("selectedItems : ", selectedItems);
  console.log("data : ", data);

  return (
    <>
      <style>
        {`
          .returned-checkbox input[type="checkbox"] {
            border: 2px solid red !important;
            border-radius: 4px;
          }
          .sticky-action-column {
            position: sticky;
            right: 0;
            background-color: white;
            z-index: 10;
            box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
          }
          .sticky-action-column th {
            background-color: ${
              Colors?.background_color || "#f8f9fa"
            } !important;
          }
        `}
      </style>
      <PageContainer>
        <FormHeader
          title={"Sales Order Return"}
          firstLabel="Dispatch"
          secondLabel="Sales Order Return"
          handleArrowClick={() => navigate("/warehouse-dispatch")}
        />

        {isLoadingInitialData ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div>
            <hr />
            <h6
              className="mb-3"
              style={{ fontWeight: "600", color: "#212529" }}
            >
              Dispatch Details
            </h6>
            <Row
              className="mb-2"
              style={{ marginLeft: "3px", marginTop: "2px" }}
            >
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Dispatch ID :</strong> {data?.dispatch_number || "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Dispatch Date & Time :</strong>{" "}
                  {data?.dispatch_date
                    ? new Date(data.dispatch_date).toLocaleString()
                    : "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Dispatch By :</strong>{" "}
                  {data?.created_by_user?.username || "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Order No :</strong> {data?.order_no || "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Invoice No :</strong> {data?.invoice_no || "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Issue From :</strong>{" "}
                  {data?.issued_from_warehouse?.warehouse_name || "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Document Uploaded :</strong>{" "}
                  {data?.document ? (
                    <button
                      onClick={() => setShowPreview(true)}
                      style={{
                        color: "blue",
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      View
                    </button>
                  ) : (
                    "No Document"
                  )}
                </p>

                <FilePreviewModal
                  show={showPreview}
                  onClose={() => setShowPreview(false)}
                  url={data?.document}
                />
              </Col>
            </Row>

            <hr />
            <div className="mb-3">
              <h6
                className="mb-3"
                style={{ fontWeight: "600", color: "#212529" }}
              >
                Customer Details
              </h6>
              <div
                style={{
                  backgroundColor: Colors?.background_color,
                  padding: "0 15px",
                  borderRadius: "8px",
                  border: `1px solid ${Colors?.background_color}`,
                }}
              >
                <Row className="mb-0 mt-3">
                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>Customer Name :</strong>{" "}
                      {data?.customer_details?.customer_name || "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>Customer Name for Transport :</strong>{" "}
                      {data?.customer_details?.customer_name_for_transport ||
                        "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>Transporter Name :</strong>{" "}
                      {data?.customer_details?.transporter_name || "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>Contact Person Name :</strong>{" "}
                      {data?.customer_details?.contact_person_name || "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>Contact Number :</strong>{" "}
                      {data?.customer_details?.contact_number || "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>Address :</strong>{" "}
                      {data?.customer_details?.address || "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>City :</strong>{" "}
                      {data?.customer_details?.city || "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>State :</strong>{" "}
                      {data?.customer_details?.state || "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>Country :</strong>{" "}
                      {data?.customer_details?.country || "-"}
                    </p>
                  </Col>

                  <Col lg={3}>
                    <p className="mb-3">
                      <strong>Pincode :</strong>{" "}
                      {data?.customer_details?.pincode || "-"}
                    </p>
                  </Col>
                </Row>
              </div>
            </div>
            <hr />
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6
                  className="mb-0"
                  style={{ fontWeight: "600", color: "#212529" }}
                >
                  Item Details
                </h6>
                {data?.dispatch_items?.length > 0 && (
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="checkbox"
                      label="Select All"
                      checked={allNonReturnedSelected}
                      onChange={handleSelectAll}
                    />
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      ({selectedItems.length} selected)
                    </span>
                  </div>
                )}
              </div>
              <div className="mb-2" style={{ overflowX: "auto" }}>
                <Table hover responsive className="align-middle text-center">
                  <thead>
                    <tr>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "50px" }}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={allNonReturnedSelected}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "80px" }}
                      >
                        Sr No.
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Dispatch Item Status
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Dispatch Qty
                      </th>
                       
                        <th
                          style={{
                            ...tableStyles.headerAlt,
                            minWidth: "150px",
                          }}
                        >
                          Return Qty
                        </th>
                      
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Items
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Item Code
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Category
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Sub Category
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Unit
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Bag No.
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Order Qty
                      </th>

                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Rate
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Amount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Discount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Discount Amount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        GST%
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        GST Amount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Total Amount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Remarks
                      </th>
                      {hasRevertableItems && (
                        <th
                          className="sticky-action-column"
                          style={{
                            ...tableStyles.headerAlt,
                            minWidth: "120px",
                          }}
                        >
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {data?.dispatch_items?.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            hasRevertableItems
                              ? 21
                              : 20
                          }
                          style={{ ...tableStyles.cell, textAlign: "center" }}
                          className="py-3"
                        >
                          No Items added. Please add at least one item.
                        </td>
                      </tr>
                    ) : (
                      data?.dispatch_items?.map((row: any, idx: number) => {
                        const itemId =
                          row.dispatch_item_id ||
                          row.id ||
                          row.dispatch_items_id;
                        const isSelected = selectedItems.includes(itemId);
                        const isReturned =
                          row?.dispatch_item_status?.toLowerCase() ===
                            "sales order return" ||
                          row?.dispatch_item_status?.toLowerCase() ===
                            "sales order return inwarded";
                        const canRevert =
                          row?.dispatch_item_status?.toLowerCase() ===
                          "sales order return";

                        return (
                          <tr
                            key={idx}
                            style={{
                              backgroundColor: isSelected
                                ? "#e3f2fd"
                                : "transparent",
                            }}
                          >
                            <td style={tableStyles.cell}>
                              {isReturned ? (
                                <div
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    backgroundColor: "#dc3545",
                                    position: "relative",
                                    margin: "0 auto",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      color: "white",
                                      fontSize: "12px",
                                      fontWeight: "bold",
                                      lineHeight: "1",
                                    }}
                                  >
                                    X
                                  </span>
                                </div>
                              ) : (
                                <Form.Check
                                  className={
                                    isReturned ? "returned-checkbox" : ""
                                  }
                                  disabled={isReturned}
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSelectItem(itemId)}
                                />
                              )}
                            </td>
                            <td style={tableStyles.cell}>{idx + 1}</td>
                            <td style={tableStyles.cell}>
                              {row?.dispatch_item_status
                                ? row.dispatch_item_status
                                    .toLowerCase()
                                    .split(" ")
                                    .map(
                                      (word: string) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1).toLowerCase()
                                    )
                                    .join(" ")
                                : "-"}
                            </td>

                            {/* Dispatch Quantity */}
                            <td style={tableStyles.cell}>
                              {row?.dispatch_quantity || "-"}
                            </td>

                            {/* Return Quantity */}
                            <td style={tableStyles.cell}>
                              {(() => {
                                const status = row?.dispatch_item_status?.toLowerCase();
                                const isReturnedOrInwarded =
                                  status === "sales order return" ||
                                  status === "sales order return inwarded";
                                
                                // Don't show return quantity for returned or inwarded items
                                if (isReturnedOrInwarded) {
                                  return row?.return_quantity || "-";
                                }
                                
                                // Show input field for selected items
                                if (isSelected) {
                                  return (
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      max={row?.dispatch_quantity || 0}
                                      step="1"
                                      value={
                                        returnQuantities[itemId] !== undefined
                                          ? returnQuantities[itemId]
                                          : ""
                                      }
                                      onChange={(e) => {
                                        const value = Math.max(0, parseInt(e.target.value, 10) || 0);
                                        setReturnQuantities((prev) => ({
                                          ...prev,
                                          [itemId]: value,
                                        }));
                                      }}
                                      style={{
                                        width: "100px",
                                        margin: "0 auto",
                                        textAlign: "center",
                                      }}
                                    />
                                  );
                                }
                                
                                // Show "-" for unselected items
                                return "-";
                              })()}
                            </td>

                            {/* Items */}
                            <td style={tableStyles.cell}>
                              {row?.item_name || "-"}
                            </td>

                            {/* Item Code */}
                            <td style={tableStyles.cell}>
                              {row?.item_code || "-"}
                            </td>

                            {/* Category */}
                            <td style={tableStyles.cell}>
                              {row?.category_name || "-"}
                            </td>

                            {/* Sub Category */}
                            <td style={tableStyles.cell}>
                              {row?.sub_category_name || "-"}
                            </td>

                            {/* Unit */}
                            <td style={tableStyles.cell}>
                              {row?.unit_name || row?.unit || "-"}
                            </td>

                            {/* Bag No. */}
                            <td style={tableStyles.cell}>
                              {row?.bags?.length > 0
                                ? row.bags.map((b: any) => b?.bag_no).join(", ")
                                : "-"}
                            </td>

                            {/* Order Quantity */}
                            <td style={tableStyles.cell}>
                              {row?.order_quantity ||
                                row?.total_quantity ||
                                "-"}
                            </td>

                            {/* Rate */}
                            <td style={tableStyles.cell}>{row?.rate || "-"}</td>

                            {/* Amount */}
                            <td style={tableStyles.cell}>
                              {row?.amount || "-"}
                            </td>

                            {/* Discount% */}
                            <td style={tableStyles.cell}>
                              {row?.discount}
                              {row?.discount_type === "percentage" ? "%" : "₹"}
                            </td>

                            {/* Discount Amount */}
                            <td style={tableStyles.cell}>
                              {row?.discount_amount || "-"}
                            </td>

                            {/* GST% */}
                            <td style={tableStyles.cell}>
                              {row?.gst_percent || "-"}
                            </td>

                            {/* GST Amount */}
                            <td style={tableStyles.cell}>
                              {row?.gst_amount || "-"}
                            </td>

                            {/* Total Amount */}
                            <td style={tableStyles.cell}>
                              {row?.total_amount || "-"}
                            </td>

                            {/* Remarks */}
                            <td style={tableStyles.cell}>
                              <TruncatedTextCell
                                text={row?.remark}
                                rowIndex={idx}
                                maxWidth="200px"
                                maxLines={2}
                              />
                            </td>

                            {/* Action Column - Sticky */}
                            <td
                              className="sticky-action-column"
                              style={tableStyles.cell}
                            >
                              {canRevert && (
                                <ActionButton
                                  label="Revert"
                                  handleClick={() => handleRevertItem(itemId)}
                                  disabled={
                                    revertingItemId === itemId || processing
                                  }
                                  // style={{
                                  //   minWidth: "80px",
                                  //   fontSize: "0.875rem",
                                  // }}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            <hr />

            <Row className="my-4">
              <Col lg={12} className="mb-3">
                <div
                  style={{
                    border: `1px solid ${Colors?.background_color}`,
                    borderRadius: "4px",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2 mb-0 py-2 px-1"
                    style={{ background: Colors?.background_color }}
                  >
                    <i
                      className="bi bi-chevron-down"
                      style={{ fontSize: "0.675rem", color: "#000" }}
                    ></i>
                    <h6 className="mb-0">Remark</h6>
                  </div>

                  <Form.Control
                    as="textarea"
                    rows={7}
                    value={data?.remark || "-"}
                    readOnly
                    style={{
                      border: "1px solid transparent",
                      borderRadius: "0px",
                      resize: "none",
                      backgroundColor: "#f8f9fa",
                      color: "#000",
                    }}
                  />
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 py-3">
              <CancelBackButton
                label="Back"
                onClick={() => {
                  navigate("/warehouse-dispatch");
                }}
                disabled={processing}
              />
              <SubmitButton
                isSubmitting={processing}
                onClick={handleSalesOrderReturn}
                disabled={processing || selectedItems.length === 0}
                labels={{
                  create: "Process Return",
                  submitting: "Processing...",
                }}
                style={{
                  minWidth: "150px",
                }}
              />
            </div>
          </div>
        )}
      </PageContainer>
    </>
  );
};

export default SalesOrderReturn;
